import React, { useState } from 'react';
import { signIn, completeNewPassword, forgotPassword, confirmForgotPassword } from 'core/services/auth.service';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { LoginSignal } from 'presentation/events';
import { mediateLoginRequester, mediateLoginInfoDependent } from 'mediators';

// ── Design tokens ────────────────────────────────────────────────────────────

const AC = {
    PURPLE: '#6344d4',
    PURPLE_STRONG: '#6d4ed5',
    PURPLE_DARK: '#5538b8',
    PURPLE_SOFT: '#ede9fe',
    PURPLE_PALE: '#c4b5fd',
    NAVY: '#1e3a8a',
    INK: '#0f172a',
    INK_700: '#334155',
    INK_600: '#475569',
    SLATE: '#64748b',
    SLATE_LIGHT: '#94a3b8',
    HAIR: '#e2e8f0',
    HAIR_SOFT: '#eef2f6',
    BG: '#f8fafc',
    BG_WEB: '#f5f3ff',
    RED: '#dc2626',
    RED_BG: '#fef2f2',
    GREEN: '#16a34a',
    GREEN_BG: '#d1fae5',
    AMBER: '#f59e0b',
    AMBER_BG: '#fef3c7',
};

// ── Icon paths ───────────────────────────────────────────────────────────────

const ICON_PATHS: Record<string, string> = {
    check:        'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z',
    check_circle: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    arrow_right:  'M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z',
    warning:      'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
    sparkle:      'M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z',
    trending_up:  'M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z',
    lock:         'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z',
    chevron_right:'M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z',
};

// ── Inline SVG icon component ────────────────────────────────────────────────

interface IconProps {
    name: string;
    size?: number;
    color?: string;
}

function Icon({ name, size = 18, color = AC.INK }: IconProps) {
    if (name === 'google') {
        return (
            <svg width={size} height={size} viewBox="0 0 48 48" style={{ display: 'inline-block', flexShrink: 0 }}>
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
            </svg>
        );
    }
    if (name === 'apple') {
        return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill={color || '#000'} style={{ display: 'inline-block', flexShrink: 0 }}>
                <path d="M17.05 20.28c-.98.95-2.05.86-3.08.43-1.09-.45-2.09-.46-3.24 0-1.44.62-2.2.44-3.06-.43C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
        );
    }
    const path = ICON_PATHS[name];
    if (!path) {
        return <span style={{ width: size, height: size, display: 'inline-block' }} />;
    }
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ display: 'inline-block', flexShrink: 0 }}>
            <path d={path} />
        </svg>
    );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const S = {
    root: { width: '100%', height: '100vh', background: '#fff', fontFamily: 'Inter, system-ui, sans-serif', color: AC.INK, display: 'flex', overflow: 'hidden' } as React.CSSProperties,

    // Left panel
    left: { flex: '0 0 480px', maxWidth: 520, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${AC.HAIR}`, padding: '28px 56px 28px', overflow: 'auto' } as React.CSSProperties,
    brand: { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 17, letterSpacing: '-0.01em', marginBottom: 48 } as React.CSSProperties,
    brandMark: { width: 32, height: 32, borderRadius: 9, background: `linear-gradient(135deg, ${AC.PURPLE_STRONG} 0%, ${AC.NAVY} 100%)`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15 } as React.CSSProperties,

    eyebrow: { fontSize: 11, fontWeight: 700, color: AC.PURPLE_STRONG, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 } as React.CSSProperties,
    title: { fontSize: 36, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: '42px' } as React.CSSProperties,
    titleHi: { color: AC.PURPLE_STRONG } as React.CSSProperties,
    sub: { fontSize: 15, color: AC.SLATE, lineHeight: '22px', marginTop: 12, maxWidth: 380 } as React.CSSProperties,

    formStack: { display: 'flex', flexDirection: 'column', gap: 14, marginTop: 32, maxWidth: 380 } as React.CSSProperties,
    field: { display: 'flex', flexDirection: 'column', gap: 6 } as React.CSSProperties,
    fieldLab: { fontSize: 12, fontWeight: 600, color: AC.INK_700 } as React.CSSProperties,

    input: { width: '100%', height: 46, border: `1px solid ${AC.HAIR}`, borderRadius: 10, padding: '0 14px', fontSize: 14, color: AC.INK, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' } as React.CSSProperties,
    inputFocused: { borderColor: AC.PURPLE_STRONG, boxShadow: `0 0 0 3px rgba(109,78,213,0.12)` } as React.CSSProperties,
    inputError: { borderColor: AC.RED, boxShadow: `0 0 0 3px rgba(220,38,38,0.10)` } as React.CSSProperties,

    primaryBtn: { height: 50, borderRadius: 10, background: AC.INK, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', border: 'none', alignSelf: 'stretch', padding: '0 22px', fontFamily: 'inherit' } as React.CSSProperties,
    primaryBtnDisabled: { background: AC.HAIR, color: AC.SLATE_LIGHT, cursor: 'not-allowed' } as React.CSSProperties,

    socialRow: { display: 'flex', gap: 10 } as React.CSSProperties,
    socialBtn: { flex: 1, height: 50, borderRadius: 10, border: `1px solid ${AC.HAIR}`, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: AC.SLATE_LIGHT, cursor: 'not-allowed', opacity: 0.6 } as React.CSSProperties,
    divider: { display: 'flex', alignItems: 'center', gap: 10, color: AC.SLATE_LIGHT, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' } as React.CSSProperties,
    dividerLine: { flex: 1, height: 1, background: AC.HAIR } as React.CSSProperties,

    errBanner: { padding: '10px 12px', background: AC.RED_BG, borderRadius: 10, fontSize: 12, color: AC.RED, display: 'flex', alignItems: 'center', gap: 6 } as React.CSSProperties,

    bottomLinks: { marginTop: 'auto', paddingTop: 32, fontSize: 13, color: AC.SLATE } as React.CSSProperties,
    createLink: { color: AC.PURPLE_STRONG, fontWeight: 700, marginLeft: 6, cursor: 'pointer' } as React.CSSProperties,

    // Right preview panel
    right: { flex: 1, background: `radial-gradient(ellipse at top right, ${AC.PURPLE_SOFT} 0%, ${AC.BG_WEB} 50%, #fff 100%)`, padding: '56px 56px', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden', position: 'relative', minWidth: 0 } as React.CSSProperties,
    previewLab: { fontSize: 11, fontWeight: 700, color: AC.SLATE, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 14 } as React.CSSProperties,
    previewCard: { background: '#fff', border: `1px solid ${AC.HAIR}`, borderRadius: 18, boxShadow: '0 24px 60px rgba(15,23,42,0.10)', padding: '24px', display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 520, alignSelf: 'center', width: '100%' } as React.CSSProperties,

    previewHead: { display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 14, borderBottom: `1px solid ${AC.HAIR}` } as React.CSSProperties,
    previewAvatar: { width: 42, height: 42, borderRadius: '50%', background: `linear-gradient(135deg, ${AC.PURPLE_STRONG}, ${AC.NAVY})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 } as React.CSSProperties,
    previewHi: { fontSize: 15, fontWeight: 700 } as React.CSSProperties,
    previewSub: { fontSize: 12, color: AC.SLATE, marginTop: 2 } as React.CSSProperties,

    statRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } as React.CSSProperties,
    statBox: { padding: '14px', borderRadius: 12, background: AC.BG, border: `1px solid ${AC.HAIR}` } as React.CSSProperties,
    statLab: { fontSize: 10, fontWeight: 700, color: AC.SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' } as React.CSSProperties,
    statVal: { fontSize: 22, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums' } as React.CSSProperties,

    sparkArea: { height: 80, position: 'relative' } as React.CSSProperties,

    entryRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${AC.HAIR_SOFT}` } as React.CSSProperties,
    entryIco: { width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } as React.CSSProperties,

    floatChip: { position: 'absolute', padding: '8px 14px', background: '#fff', borderRadius: 24, boxShadow: '0 8px 24px rgba(15,23,42,0.10)', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${AC.HAIR}` } as React.CSSProperties,
};

// ── Right preview panel ──────────────────────────────────────────────────────

function PreviewCard() {
    const entries = [
        { t: 'Rent', a: '-$1,800', iconName: 'lock', bg: AC.PURPLE_SOFT, fg: AC.PURPLE_STRONG },
        { t: 'Salary', a: '+$2,480', iconName: 'trending_up', bg: AC.GREEN_BG, fg: AC.GREEN },
        { t: 'Hydro-Québec', a: '-$156', iconName: 'sparkle', bg: '#FEF3C7', fg: '#D97706' },
    ];

    return (
        <div style={S.right}>
            <div style={{ ...S.floatChip, top: 60, right: 80, color: AC.GREEN }}>
                <Icon name="check_circle" size={14} color={AC.GREEN} />
                On track for May
            </div>
            <div style={{ ...S.floatChip, bottom: 80, left: 80, color: AC.PURPLE_STRONG }}>
                <Icon name="sparkle" size={14} color={AC.PURPLE_STRONG} />
                AI suggestion ready
            </div>

            <div style={S.previewLab}>What you'll see in Uplift</div>
            <div style={S.previewCard}>
                <div style={S.previewHead}>
                    <div style={S.previewAvatar}>H</div>
                    <div style={{ flex: 1 }}>
                        <div style={S.previewHi}>Hi, You</div>
                        <div style={S.previewSub}>Here's your month at a glance</div>
                    </div>
                    <Icon name="sparkle" size={18} color={AC.PURPLE_STRONG} />
                </div>

                <div style={S.statRow}>
                    <div style={S.statBox}>
                        <div style={S.statLab}>End of month</div>
                        <div style={{ ...S.statVal, color: AC.GREEN }}>+$2,140</div>
                    </div>
                    <div style={S.statBox}>
                        <div style={S.statLab}>Saved this year</div>
                        <div style={S.statVal}>$8,420</div>
                    </div>
                </div>

                <div style={S.sparkArea}>
                    <svg width="100%" height="80" viewBox="0 0 480 80" preserveAspectRatio="none">
                        <defs>
                            <linearGradient id="ln-g1" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0" stopColor={AC.PURPLE_STRONG} stopOpacity={0.30} />
                                <stop offset="1" stopColor={AC.PURPLE_STRONG} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <path d="M0,60 C40,52 80,30 130,38 C180,46 220,18 280,22 C340,26 380,52 480,12 L480,80 L0,80 Z" fill="url(#ln-g1)" />
                        <path d="M0,60 C40,52 80,30 130,38 C180,46 220,18 280,22 C340,26 380,52 480,12" stroke={AC.PURPLE_STRONG} strokeWidth="2.5" fill="none" />
                        {([[40, 52], [130, 38], [280, 22], [380, 52], [460, 16]] as [number, number][]).map(([x, y], i) => (
                            <circle key={i} cx={x} cy={y} r="3" fill="#fff" stroke={AC.PURPLE_STRONG} strokeWidth="2" />
                        ))}
                    </svg>
                </div>

                <div>
                    {entries.map((entry, i) => {
                        const amountColor = entry.a[0] === '+' ? AC.GREEN : AC.INK;
                        return (
                            <div key={i} style={S.entryRow}>
                                <div style={{ ...S.entryIco, background: entry.bg }}>
                                    <Icon name={entry.iconName} size={14} color={entry.fg} />
                                </div>
                                <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{entry.t}</div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: amountColor }}>{entry.a}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ── Login component ──────────────────────────────────────────────────────────

export const Login = () => {
    const dispatch = useDispatch();
    const [loginSignal] = useState(new LoginSignal());
    const login: any | undefined = useSelector(mediateLoginInfoDependent);
    const history = useHistory();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [forgotStep, setForgotStep] = useState<'none' | 'email' | 'code'>('none');
    const [resetCode, setResetCode] = useState('');
    const [newPw, setNewPw] = useState('');
    const [forgotSuccess, setForgotSuccess] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [pendingUser, setPendingUser] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [emailFocused, setEmailFocused] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [newPasswordFocused, setNewPasswordFocused] = useState(false);

    React.useEffect(() => {
        return mediateLoginRequester(loginSignal, dispatch).destroy;
    }, [loginSignal, dispatch]);

    React.useEffect(() => {
        if (login) {
            history.push('/home');
        }
    }, [login, history]);

    const dispatchLogin = (loginData: any) => {
        loginSignal.dispatch(loginData);
        //@ts-ignore
        if (window.ReactNativeWebView) {
            //@ts-ignore
            window.ReactNativeWebView.postMessage(JSON.stringify(loginData));
        }
    };

    const handleSignIn = async (evt: React.FormEvent) => {
        evt.preventDefault();
        setLoading(true);
        setError('');
        try {
            const loginData = await signIn(email, password);
            dispatchLogin(loginData);
        } catch (err: any) {
            if (err.challengeName === 'NEW_PASSWORD_REQUIRED') {
                setPendingUser(err.cognitoUser);
            } else {
                setError(err.message || 'Sign in failed');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleNewPassword = async (evt: React.FormEvent) => {
        evt.preventDefault();
        setLoading(true);
        setError('');
        try {
            const loginData = await completeNewPassword(pendingUser, newPassword);
            dispatchLogin(loginData);
        } catch (err: any) {
            setError(err.message || 'Failed to set new password');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotSendCode = async (evt: React.FormEvent) => {
        evt.preventDefault();
        if (!email) { setError('Enter your email first'); return; }
        setLoading(true);
        setError('');
        try {
            await forgotPassword(email);
            setForgotStep('code');
        } catch (err: any) {
            setError(err.message || 'Failed to send reset code');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotConfirm = async (evt: React.FormEvent) => {
        evt.preventDefault();
        setLoading(true);
        setError('');
        try {
            await confirmForgotPassword(email, resetCode, newPw);
            setForgotSuccess(true);
            setForgotStep('none');
            setResetCode('');
            setNewPw('');
        } catch (err: any) {
            setError(err.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    const hasError = error.length > 0;
    const emailInputStyle = {
        ...S.input,
        ...(hasError ? S.inputError : emailFocused ? S.inputFocused : {}),
    };
    const passwordInputStyle = {
        ...S.input,
        ...(hasError ? S.inputError : passwordFocused ? S.inputFocused : {}),
    };
    const signInBtnStyle = loading
        ? { ...S.primaryBtn, ...S.primaryBtnDisabled }
        : S.primaryBtn;

    // ── New password challenge screen ──────────────────────────────────────

    if (pendingUser) {
        const newPasswordInputStyle = {
            ...S.input,
            ...(newPasswordFocused ? S.inputFocused : {}),
        };
        const newPasswordBtnStyle = loading
            ? { ...S.primaryBtn, ...S.primaryBtnDisabled }
            : S.primaryBtn;

        return (
            <div style={S.root}>
                {/* Left panel */}
                <div style={S.left}>
                    <div style={S.brand}>
                        <div style={S.brandMark}>U</div>
                        Uplift
                    </div>

                    <form onSubmit={handleNewPassword}>
                        <div style={S.eyebrow}>Action required</div>
                        <div style={S.title}>
                            Set a new<br /><span style={S.titleHi}>password.</span>
                        </div>
                        <div style={S.sub}>Your account requires a one-time password change to continue.</div>

                        <div style={S.formStack}>
                            <div style={S.field}>
                                <div style={S.fieldLab}>New password</div>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    onFocus={() => setNewPasswordFocused(true)}
                                    onBlur={() => setNewPasswordFocused(false)}
                                    style={newPasswordInputStyle}
                                    required
                                    autoFocus
                                    aria-label="New password"
                                    data-testid="new-password-input"
                                />
                            </div>

                            {error && (
                                <div style={S.errBanner} data-testid="error-banner">
                                    <Icon name="warning" size={13} color={AC.RED} />
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                style={newPasswordBtnStyle}
                                disabled={loading}
                                data-testid="set-password-btn"
                            >
                                {loading ? 'Setting password…' : 'Set password'}
                                {!loading && <Icon name="arrow_right" size={16} color="#fff" />}
                            </button>
                        </div>
                    </form>

                    <div style={S.bottomLinks}>
                        New to Uplift?
                        <span
                            style={S.createLink}
                            onClick={() => history.push('/signup')}
                            role="link"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter') history.push('/signup'); }}
                            data-testid="create-account-link"
                        >
                            Create account
                        </span>
                    </div>
                </div>

                {/* Right preview pane */}
                <PreviewCard />
            </div>
        );
    }

    // ── Main sign-in screen ────────────────────────────────────────────────

    return (
        <div style={S.root}>
            {/* Left panel */}
            <div style={S.left}>
                <div style={S.brand}>
                    <div style={S.brandMark}>U</div>
                    Uplift
                </div>

                <form onSubmit={handleSignIn}>
                    <div style={S.eyebrow} data-testid="eyebrow">Welcome back</div>
                    <div style={S.title}>
                        Pick up where<br /><span style={S.titleHi}>you left off.</span>
                    </div>
                    <div style={S.sub}>Your forecast is waiting. Sign in to see what's changed.</div>

                    <div style={S.formStack}>
                        {/* Social buttons — disabled, coming soon */}
                        <div style={S.socialRow}>
                            <button
                                type="button"
                                style={S.socialBtn}
                                disabled
                                title="Coming soon"
                                data-testid="apple-btn"
                            >
                                <Icon name="apple" size={16} color={AC.SLATE_LIGHT} />
                                Apple
                                <span style={{ fontSize: 10, color: AC.SLATE_LIGHT, fontWeight: 600 }}>Soon</span>
                            </button>
                            <button
                                type="button"
                                style={S.socialBtn}
                                disabled
                                title="Coming soon"
                                data-testid="google-btn"
                            >
                                <Icon name="google" size={16} />
                                Google
                                <span style={{ fontSize: 10, color: AC.SLATE_LIGHT, fontWeight: 600 }}>Soon</span>
                            </button>
                        </div>

                        <div style={S.divider}>
                            <span style={S.dividerLine} />
                            or with email
                            <span style={S.dividerLine} />
                        </div>

                        {/* Error banner */}
                        {error && (
                            <div style={S.errBanner} data-testid="error-banner">
                                <Icon name="warning" size={13} color={AC.RED} />
                                Email or password is incorrect.
                            </div>
                        )}

                        {/* Email field */}
                        <div style={S.field}>
                            <div style={S.fieldLab}>Email</div>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onFocus={() => setEmailFocused(true)}
                                onBlur={() => setEmailFocused(false)}
                                style={emailInputStyle}
                                required
                                autoFocus
                                placeholder="you@example.com"
                                aria-label="Email address"
                                data-testid="email-input"
                            />
                        </div>

                        {/* Password field with Forgot link */}
                        <div style={S.field}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={S.fieldLab}>Password</div>
                                <span
                                    style={{ fontSize: 12, color: AC.PURPLE_STRONG, fontWeight: 700, cursor: 'pointer' }}
                                    data-testid="forgot-link"
                                    onClick={() => { setForgotStep('email'); setError(''); setForgotSuccess(false); }}
                                >
                                    Forgot?
                                </span>
                            </div>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onFocus={() => setPasswordFocused(true)}
                                onBlur={() => setPasswordFocused(false)}
                                style={passwordInputStyle}
                                required
                                aria-label="Password"
                                data-testid="password-input"
                            />
                        </div>

                        {/* Sign in button */}
                        <button
                            type="submit"
                            style={signInBtnStyle}
                            disabled={loading}
                            data-testid="signin-btn"
                        >
                            {loading ? 'Signing in…' : 'Sign in'}
                            {!loading && <Icon name="arrow_right" size={16} color="#fff" />}
                        </button>
                    </div>
                </form>

                <div style={S.bottomLinks}>
                    New to Uplift?
                    <span
                        style={S.createLink}
                        onClick={() => history.push('/signup')}
                        role="link"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') history.push('/signup'); }}
                        data-testid="create-account-link"
                    >
                        Create account
                    </span>
                </div>
            </div>

            {/* Right preview pane */}
            <PreviewCard />

            {/* Forgot password modal */}
            {forgotStep !== 'none' && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 30 }} onClick={() => setForgotStep('none')}>
                    <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(0,0,0,0.20)', padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 16 }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ fontSize: 18, fontWeight: 800 }}>
                                {forgotStep === 'email' ? 'Reset password' : 'Enter code'}
                            </div>
                            <span onClick={() => setForgotStep('none')} style={{ cursor: 'pointer', color: AC.SLATE }}>
                                <Icon name="close" size={18} color={AC.SLATE} />
                            </span>
                        </div>

                        {forgotStep === 'email' && (
                            <form onSubmit={handleForgotSendCode} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <div style={{ fontSize: 13, color: AC.SLATE }}>
                                    Enter your email and we'll send a 6-digit code to reset your password.
                                </div>
                                <div style={S.field}>
                                    <div style={S.fieldLab}>Email</div>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        style={{ ...S.input, fontSize: 16, fontWeight: 600 }}
                                        autoFocus
                                        required
                                    />
                                </div>
                                {error && <div style={S.errBanner}><Icon name="warning" size={13} color={AC.RED} />{error}</div>}
                                <button type="submit" disabled={loading} style={{ ...S.primaryBtn, width: '100%', justifyContent: 'center' }}>
                                    {loading ? 'Sending…' : 'Send reset code'}
                                </button>
                            </form>
                        )}

                        {forgotStep === 'code' && (
                            <form onSubmit={handleForgotConfirm} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <div style={{ fontSize: 13, color: AC.SLATE }}>
                                    Code sent to <strong style={{ color: AC.INK }}>{email}</strong>. Enter it below with your new password.
                                </div>
                                <div style={S.field}>
                                    <div style={S.fieldLab}>6-digit code</div>
                                    <input
                                        type="text"
                                        value={resetCode}
                                        onChange={e => setResetCode(e.target.value)}
                                        placeholder="123456"
                                        style={{ ...S.input, letterSpacing: '0.2em', textAlign: 'center' as const }}
                                        autoFocus
                                        required
                                    />
                                </div>
                                <div style={S.field}>
                                    <div style={S.fieldLab}>New password</div>
                                    <input
                                        type="password"
                                        value={newPw}
                                        onChange={e => setNewPw(e.target.value)}
                                        style={{ ...S.input, fontSize: 16, fontWeight: 600 }}
                                        required
                                    />
                                </div>
                                {error && <div style={S.errBanner}><Icon name="warning" size={13} color={AC.RED} />{error}</div>}
                                <button type="submit" disabled={loading} style={{ ...S.primaryBtn, width: '100%', justifyContent: 'center' }}>
                                    {loading ? 'Resetting…' : 'Reset password'}
                                </button>
                                <div style={{ fontSize: 12, color: AC.SLATE, textAlign: 'center' as const }}>
                                    Didn't get it?{' '}
                                    <span onClick={() => { setForgotStep('email'); setError(''); }} style={{ color: AC.PURPLE_STRONG, fontWeight: 700, cursor: 'pointer' }}>
                                        Resend
                                    </span>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Success toast */}
            {forgotSuccess && (
                <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#D1FAE5', color: '#065F46', padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.10)', zIndex: 40 }}>
                    <Icon name="check_circle" size={16} color="#065F46" />
                    Password reset. Sign in with your new password.
                    <span onClick={() => setForgotSuccess(false)} style={{ cursor: 'pointer', marginLeft: 8 }}>
                        <Icon name="close" size={14} color="#065F46" />
                    </span>
                </div>
            )}
        </div>
    );
};
